import React, { useState, useEffect } from 'react';

export default function LandingPage({ onLogin, onRegister }) {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Read referral code from URL if present
  const params = new URLSearchParams(window.location.search);
  const refCode = params.get('ref');

  const handleGetStarted = () => {
    if (refCode) {
      window.location.href = `/register?ref=${refCode}`;
    } else {
      onRegister();
    }
  };

  const faqs = [
    {
      q: 'How much time does this actually save me every month?',
      a: 'Most landlords spend 2-4 hours a month on rent follow-ups, payment confirmations, and receipt generation. With RentOS, that drops to under 10 minutes — just reviewing and approving payments as they come in. The reminders, receipts, and record-keeping happen automatically.'
    },
    {
      q: 'What if a tenant sends a screenshot I can\'t verify?',
      a: 'RentOS shows you the extracted payment details — amount, transaction ID, and date — before you approve. If anything looks wrong, you reject it with a reason and the tenant is notified to resubmit. You stay in full control.'
    },
    {
      q: 'Do my tenants need to learn anything new?',
      a: 'Nothing. They just pay via GPay, PhonePe, or any UPI app as usual — then send the screenshot to the RentOS WhatsApp number. That\'s it. No app, no account, no learning curve for them.'
    },
    {
      q: 'What if a tenant pays cash or by cheque?',
      a: 'You can manually record any payment — cash, cheque, bank transfer — directly from your dashboard. A receipt is generated and sent to the tenant on WhatsApp just like any other payment.'
    },
    {
      q: 'Can I manage multiple properties from one account?',
      a: 'Yes. Add as many buildings and units as you need. Each gets its own overview — paid, unpaid, arrears, reminders. Switch between properties in one click from your dashboard.'
    },
    {
      q: 'How do I get started?',
      a: 'RentOS is currently invite-only to ensure quality. Get a referral code from an existing RentOS landlord, or contact us directly. Registration takes under 2 minutes with a WhatsApp OTP — no forms, no documents.'
    },
  ];

  const features = [
    {
      icon: '📋',
      title: 'Know Who Paid. Instantly.',
      desc: 'Your dashboard shows paid, unpaid, and upcoming rent — every month, every tenant, every building. No spreadsheet. No guesswork.'
    },
    {
      icon: '🔔',
      title: 'Reminders Send Themselves.',
      desc: 'Automated WhatsApp reminders go out on the 28th, 5th, and 10th. You never have to personally chase a tenant for rent again.'
    },
    {
      icon: '📊',
      title: 'Arrears at a Glance.',
      desc: 'See exactly how much is outstanding across all your units. Send reminders to everyone with one click — not one by one.'
    },
    {
      icon: '🧾',
      title: 'Receipts Without the Work.',
      desc: 'Professional PDF receipts generated and delivered to tenants automatically. They stop asking you. You stop making them manually.'
    },
    {
      icon: '📱',
      title: 'Your Tenants Need Zero Apps.',
      desc: 'Everything runs on WhatsApp — which your tenants already use. No onboarding. No passwords. No support calls to you.'
    },
    {
      icon: '🏢',
      title: 'All Your Buildings. One Dashboard.',
      desc: 'Manage multiple buildings, units, and tenants from one place. Switch between properties in one click.'
    },
  ];

  const steps = [
    {
      num: '01',
      title: 'Add Your Properties & Tenants',
      desc: 'Register in 2 minutes. Add your buildings, units, rent amounts, and tenant details. Each tenant gets a WhatsApp welcome message automatically — no calls needed from you.'
    },
    {
      num: '02',
      title: 'Tenant Pays — You Get Notified',
      desc: 'Tenant pays via UPI and sends the screenshot on WhatsApp. RentOS reads it, extracts the details, and sends you a confirmation to approve. One tap — done.'
    },
    {
      num: '03',
      title: 'Receipt Goes Out. You Do Nothing.',
      desc: 'The moment you approve, tenant gets a professional PDF receipt on WhatsApp. HRA-valid. Auto-numbered. No printing. No WhatsApp forwards. No Excel sheets.'
    },
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: '#0F1F33', overflowX: 'hidden' }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .hero-title {
          font-family: 'Sora', sans-serif;
          font-size: clamp(36px, 6vw, 72px);
          font-weight: 800;
          line-height: 1.1;
          color: #0F1F33;
        }

        .section-title {
          font-family: 'Sora', sans-serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 700;
          line-height: 1.2;
        }

        .nav-link {
          color: #4A5568;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }
        .nav-link:hover { color: #1C4E80; }

        .btn-primary {
          background: #D85A30;
          color: white;
          border: none;
          padding: 14px 28px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          transition: all 0.2s;
          text-decoration: none;
          display: inline-block;
        }
        .btn-primary:hover {
          background: #C04E28;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(216, 90, 48, 0.35);
        }

        .btn-secondary {
          background: white;
          color: #1C4E80;
          border: 2px solid #1C4E80;
          padding: 13px 28px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          transition: all 0.2s;
        }
        .btn-secondary:hover {
          background: #1C4E80;
          color: white;
          transform: translateY(-1px);
        }

        .btn-nav {
          background: #1C4E80;
          color: white;
          border: none;
          padding: 9px 20px;
          border-radius: 7px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          transition: all 0.2s;
        }
        .btn-nav:hover { background: #163d65; }

        .feature-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          border: 1px solid #E8EFF7;
          transition: all 0.25s;
        }
        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(28, 78, 128, 0.1);
          border-color: #1C4E80;
        }

        .step-card {
          background: white;
          border-radius: 16px;
          padding: 32px;
          border: 1px solid #E8EFF7;
          position: relative;
          overflow: hidden;
        }
        .step-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 4px; height: 100%;
          background: #D85A30;
        }

        .faq-item {
          border-bottom: 1px solid #E8EFF7;
        }
        .faq-question {
          width: 100%;
          background: none;
          border: none;
          text-align: left;
          padding: 20px 0;
          font-size: 16px;
          font-weight: 600;
          color: #0F1F33;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }
        .faq-question:hover { color: #1C4E80; }

        .whatsapp-float {
          position: fixed;
          bottom: 28px;
          right: 28px;
          width: 56px;
          height: 56px;
          background: #25D366;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(37, 211, 102, 0.4);
          cursor: pointer;
          z-index: 1000;
          transition: all 0.2s;
          text-decoration: none;
        }
        .whatsapp-float:hover {
          transform: scale(1.1);
          box-shadow: 0 12px 32px rgba(37, 211, 102, 0.5);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #EBF2FA;
          color: #1C4E80;
          padding: 6px 14px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 24px;
        }

        .orange-highlight {
          color: #D85A30;
          position: relative;
        }

        .trust-bar {
          background: #F7F9FC;
          border-top: 1px solid #E8EFF7;
          border-bottom: 1px solid #E8EFF7;
          padding: 20px 0;
        }

        .trust-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .trust-number {
          font-family: 'Sora', sans-serif;
          font-size: 28px;
          font-weight: 800;
          color: #1C4E80;
        }

        .trust-label {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .fade-up {
          animation: fadeUp 0.6s ease forwards;
        }

        .fade-up-1 { animation-delay: 0.1s; opacity: 0; }
        .fade-up-2 { animation-delay: 0.25s; opacity: 0; }
        .fade-up-3 { animation-delay: 0.4s; opacity: 0; }
        .fade-up-4 { animation-delay: 0.55s; opacity: 0; }

        .phone-mockup {
          background: #1C4E80;
          border-radius: 32px;
          padding: 16px;
          box-shadow: 0 32px 80px rgba(28, 78, 128, 0.3);
          max-width: 280px;
          margin: 0 auto;
        }

        .phone-screen {
          background: white;
          border-radius: 20px;
          overflow: hidden;
        }

        .wa-header {
          background: #1C4E80;
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .wa-bubble {
          margin: 8px 12px;
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 12px;
          line-height: 1.5;
          max-width: 85%;
        }

        .wa-received {
          background: #f0f0f0;
          color: #333;
          border-top-left-radius: 4px;
          align-self: flex-start;
        }

        .wa-sent {
          background: #DCF8C6;
          color: #333;
          border-top-right-radius: 4px;
          margin-left: auto;
        }

        .wa-system {
          background: #E8F4FD;
          color: #1C4E80;
          margin: 6px auto;
          font-size: 11px;
          text-align: center;
          border-radius: 8px;
          padding: 8px 12px;
          max-width: 90%;
        }

        @media (max-width: 768px) {
          .hero-grid { flex-direction: column !important; }
          .features-grid { grid-template-columns: 1fr 1fr !important; }
          .steps-grid { grid-template-columns: 1fr !important; }
          .trust-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }
          .nav-links { display: none !important; }
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }

        @media (max-width: 480px) {
          .features-grid { grid-template-columns: 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        zIndex: 999,
        background: scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid #E8EFF7' : 'none',
        transition: 'all 0.3s',
        padding: '0 24px',
      }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          height: 64, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img
              src="/rentos_logo.svg"
              alt="RentOS"
              style={{ height: 44, width: 'auto' }}
            />
          </div>

          {/* Nav Links */}
          <div className="nav-links" style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="#faq" className="nav-link">FAQ</a>
            <a href="https://rentos.co.in/privacy-policy" className="nav-link">Privacy</a>
          </div>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="nav-link" onClick={onLogin}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#4A5568' }}>
              Login
            </button>
            <button className="btn-nav" onClick={handleGetStarted}>
              Get Started →
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        background: 'linear-gradient(135deg, #F7FAFF 0%, #EBF2FA 50%, #FFF5F0 100%)',
        paddingTop: 120, paddingBottom: 80,
        minHeight: '92vh',
        display: 'flex', alignItems: 'center'
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', width: '100%' }}>
          <div className="hero-grid" style={{ display: 'flex', alignItems: 'center', gap: 64 }}>

            {/* Left — Text */}
            <div style={{ flex: 1 }}>
              <div className={`hero-badge ${visible ? 'fade-up fade-up-1' : ''}`}>
                <span>🏠</span> For Indian Landlords with 1–20 Units
              </div>

              <h1 className={`hero-title ${visible ? 'fade-up fade-up-2' : ''}`}>
                Stop chasing tenants.<br />
                Let <span className="orange-highlight">RentOS</span> run<br />
                your rentals.
              </h1>

              <p className={`${visible ? 'fade-up fade-up-3' : ''}`} style={{
                marginTop: 24, marginBottom: 36,
                fontSize: 18, color: '#4A5568',
                lineHeight: 1.7, maxWidth: 480
              }}>
                Tenant pays on WhatsApp — RentOS reads the screenshot,
                notifies you, and sends the receipt automatically.
                No manual work. No follow-ups. No missed payments.
              </p>

              <div className={`${visible ? 'fade-up fade-up-4' : ''}`}
                style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <button className="btn-primary" onClick={handleGetStarted}>
                  Get Started Free →
                </button>
                <button className="btn-secondary" onClick={onLogin}>
                  Login to Dashboard
                </button>
              </div>

              <p style={{ marginTop: 20, fontSize: 13, color: '#94a3b8' }}>
                Invite-only · Register with a referral code · rentos.co.in
              </p>
            </div>

            {/* Right — Phone Mockup */}
            <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
              <div className="phone-mockup">
                <div className="phone-screen">
                  {/* WA Header */}
                  <div className="wa-header">
                    <div style={{
                      width: 36, height: 36,
                      background: 'white',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <img src="/rentos_logo.svg" alt="RentOS"
                        style={{ width: 32, height: 32, objectFit: 'contain' }} />
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>RentOS</div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>+91 93803 18018</div>
                    </div>
                  </div>

                  {/* Chat bubbles */}
                  <div style={{ padding: '8px 0', background: '#ECE5DD', minHeight: 340 }}>
                    <div className="wa-bubble wa-received">
                      Hi Iyappan! Welcome to RentOS 🎉<br />
                      Room 1 · Rent ₹8,000/month<br />
                      Send payment screenshots here.
                    </div>

                    <div style={{ margin: '10px 12px', textAlign: 'right' }}>
                      <div style={{
                        display: 'inline-block',
                        background: '#DCF8C6',
                        borderRadius: '12px 12px 4px 12px',
                        padding: '8px 12px',
                        fontSize: 12
                      }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>📷 GPay screenshot</div>
                        <div style={{ fontWeight: 600 }}>₹8,000 payment</div>
                      </div>
                    </div>

                    <div className="wa-bubble wa-received">
                      ✅ Payment received!<br />
                      Amount: ₹8,000<br />
                      Month: March-2026<br />
                      <br />
                      Pending landlord confirmation.
                    </div>

                    <div className="wa-system">
                      🧾 Receipt Ready
                    </div>

                    <div className="wa-bubble wa-received">
                      Your rent receipt is ready!<br />
                      Receipt No: ROS-2026-04-2679<br />
                      <br />
                      <span style={{ color: '#1C4E80', fontWeight: 600 }}>
                        📄 Download PDF →
                      </span><br />
                      <span style={{ fontSize: 10, color: '#888' }}>
                        Valid for HRA tax exemption
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <div className="trust-bar">
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          <div className="trust-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 32, textAlign: 'center'
          }}>
            {[
              { num: '0', label: 'manual receipts to generate' },
              { num: '0', label: 'calls from tenants asking for receipts' },
              { num: '60s', label: 'from payment to receipt — automatic' },
              { num: '24/7', label: 'rent tracking without your involvement' },
            ].map((item, i) => (
              <div key={i} className="trust-item">
                <span className="trust-number">{item.num}</span>
                <span className="trust-label">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={{
        padding: '80px 24px',
        background: 'white'
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ color: '#D85A30', fontWeight: 600, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              Takes 10 minutes to set up
            </p>
            <h2 className="section-title">You set it up once.<br/>RentOS does the rest.</h2>
            <p style={{ color: '#64748b', marginTop: 16, fontSize: 16, maxWidth: 480, margin: '16px auto 0' }}>
              After the first setup, your tenants handle their side on WhatsApp. You just approve.
            </p>
          </div>

          <div className="steps-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24
          }}>
            {steps.map((step, i) => (
              <div key={i} className="step-card">
                <div style={{
                  fontFamily: 'Sora', fontSize: 48,
                  fontWeight: 800, color: '#EBF2FA',
                  lineHeight: 1, marginBottom: 16
                }}>{step.num}</div>
                <h3 style={{
                  fontFamily: 'Sora', fontSize: 18,
                  fontWeight: 700, marginBottom: 12, color: '#1C4E80'
                }}>{step.title}</h3>
                <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{
        padding: '80px 24px',
        background: '#F7F9FC'
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ color: '#D85A30', fontWeight: 600, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              Less work for you
            </p>
            <h2 className="section-title">Everything that used to eat your time — automated.</h2>
            <p style={{ color: '#64748b', marginTop: 16, fontSize: 16, maxWidth: 520, margin: '16px auto 0' }}>
              RentOS handles the follow-ups, receipts, reminders, and record keeping. You just collect rent.
            </p>
          </div>

          <div className="features-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 20
          }}>
            {features.map((f, i) => (
              <div key={i} className="feature-card">
                <div style={{ fontSize: 32, marginBottom: 16 }}>{f.icon}</div>
                <h3 style={{
                  fontFamily: 'Sora', fontSize: 17,
                  fontWeight: 700, marginBottom: 10, color: '#1C4E80'
                }}>{f.title}</h3>
                <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.65 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HRA HIGHLIGHT BAND ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1C4E80 0%, #0F2D4A 100%)',
        padding: '64px 24px',
        textAlign: 'center'
      }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>😮‍💨</div>
          <h2 style={{
            fontFamily: 'Sora', fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: 700, color: 'white', marginBottom: 16
          }}>
            "Can you send me a rent receipt<br />for my HRA claim?"
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.75)', fontSize: 16,
            lineHeight: 1.7, marginBottom: 32
          }}>
            Every landlord hears this every January and March. Tenants calling, texting,
            following up for receipts they need for their tax exemption.
            With RentOS — every payment automatically generates a professional,
            HRA-valid PDF receipt and delivers it to the tenant on WhatsApp.
            They never need to ask you again.
          </p>
          <button className="btn-primary" onClick={handleGetStarted}
            style={{ background: '#D85A30' }}>
            End the Receipt Requests →
          </button>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ color: '#D85A30', fontWeight: 600, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              Got questions?
            </p>
            <h2 className="section-title">Frequently asked</h2>
          </div>

          {faqs.map((faq, i) => (
            <div key={i} className="faq-item">
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{faq.q}</span>
                <span style={{
                  fontSize: 20, color: '#1C4E80',
                  transition: 'transform 0.2s',
                  transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)',
                  flexShrink: 0
                }}>+</span>
              </button>
              {openFaq === i && (
                <div style={{
                  paddingBottom: 20, color: '#4A5568',
                  fontSize: 15, lineHeight: 1.7
                }}>
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{
        background: 'linear-gradient(135deg, #FFF5F0 0%, #EBF2FA 100%)',
        padding: '80px 24px', textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 className="section-title" style={{ marginBottom: 16 }}>
            Your rentals shouldn't<br />feel like a second job.
          </h2>
          <p style={{ color: '#64748b', fontSize: 16, lineHeight: 1.7, marginBottom: 36 }}>
            No more monthly reminders, manual receipts, payment follow-ups,
            or Excel tracking. RentOS handles it all — quietly, automatically,
            on WhatsApp. You just collect rent.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={handleGetStarted}>
              Get Started — It's Free →
            </button>
            <button className="btn-secondary" onClick={onLogin}>
              Login to Dashboard
            </button>
          </div>
          <p style={{ marginTop: 20, fontSize: 13, color: '#94a3b8' }}>
            Invite-only beta · rentos.co.in · Hashvi Ventures Private Limited
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        background: '#0F1F33', color: 'rgba(255,255,255,0.7)',
        padding: '48px 24px 32px'
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="footer-grid" style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: 40, marginBottom: 48
          }}>
            {/* Brand */}
            <div>
              <div style={{ marginBottom: 16 }}>
                <img
                  src="/rentos_logo.svg"
                  alt="RentOS"
                  style={{ height: 48, width: 'auto' }}
                />
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 260 }}>
                Automated rent collection, receipts, and reminders — so you can be a landlord without the landlord headaches.
              </p>
              <p style={{ fontSize: 13, marginTop: 16, color: 'rgba(255,255,255,0.4)' }}>
                Hashvi Ventures Private Limited
              </p>
            </div>

            {/* Product */}
            <div>
              <p style={{ color: 'white', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Product</p>
              {[
                { label: 'Features', href: '#features' },
                { label: 'How it works', href: '#how-it-works' },
                { label: 'FAQ', href: '#faq' },
                { label: 'Dashboard', action: onLogin },
              ].map((link, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  {link.href ? (
                    <a href={link.href} style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, textDecoration: 'none' }}
                      onMouseOver={e => e.target.style.color = 'white'}
                      onMouseOut={e => e.target.style.color = 'rgba(255,255,255,0.6)'}>
                      {link.label}
                    </a>
                  ) : (
                    <button onClick={link.action}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 14, cursor: 'pointer', padding: 0 }}>
                      {link.label}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Company */}
            <div>
              <p style={{ color: 'white', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Company</p>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Contact', href: 'mailto:support@rentos.co.in' },
              ].map((link, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <a href={link.href} style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, textDecoration: 'none' }}
                    onMouseOver={e => e.target.style.color = 'white'}
                    onMouseOut={e => e.target.style.color = 'rgba(255,255,255,0.6)'}>
                    {link.label}
                  </a>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div>
              <p style={{ color: 'white', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Contact</p>
              <p style={{ fontSize: 14, marginBottom: 8 }}>support@rentos.co.in</p>
              <p style={{ fontSize: 14, marginBottom: 16 }}>+91 93803 18018</p>
              <a
                href={`https://wa.me/919380318018?text=Hi, I need more details about RentOS`}
                target="_blank" rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#25D366', color: 'white',
                  padding: '8px 16px', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, textDecoration: 'none'
                }}>
                💬 Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            paddingTop: 24,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: 12
          }}>
            <p style={{ fontSize: 13 }}>
              © 2026 Hashvi Ventures Private Limited. All rights reserved.
            </p>
            <p style={{ fontSize: 13 }}>
              Made with ❤️ in India
            </p>
          </div>
        </div>
      </footer>

      {/* ── FLOATING WHATSAPP BUTTON ── */}
      <a
        href={`https://wa.me/919380318018?text=Hi, I need more details about RentOS`}
        target="_blank" rel="noreferrer"
        className="whatsapp-float"
        title="Chat with us on WhatsApp"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
